import { useEffect, useRef } from "react";

import {
  homepageImageUrl,
  type HomepageIdentityContent,
  type HomepageImage,
} from "@/data/homepageContent";

const MOBILE_MEDIA = "(max-width: 767px)";

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

type IdentitySectionProps = {
  content: HomepageIdentityContent;
};

const backgroundStyle = (image: HomepageImage) => ({
  backgroundImage: `url('${homepageImageUrl(image)}')`,
  backgroundPosition: image.position,
});

export function IdentitySection({ content }: IdentitySectionProps) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const mediaFrameRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const shell = shellRef.current;
    const mediaFrame = mediaFrameRef.current;

    if (!shell || !mediaFrame) {
      return;
    }

    const mobileQuery = window.matchMedia(MOBILE_MEDIA);
    let frameId: number | null = null;

    const render = () => {
      frameId = null;

      if (!mobileQuery.matches) {
        mediaFrame.style.removeProperty("--story-mobile-image-progress");
        mediaFrame.style.removeProperty("--story-mobile-incoming-scale");
        mediaFrame.style.removeProperty("--story-mobile-outgoing-scale");
        mediaFrame.style.removeProperty("--story-mobile-outgoing-opacity");
        return;
      }

      const section = shell.closest<HTMLElement>("#identity") ?? shell;
      const rect = section.getBoundingClientRect();
      const rawProgress = clamp(
        (-rect.top + window.innerHeight * 0.08) / (window.innerHeight * 0.28),
        0,
        1,
      );
      const easedProgress = rawProgress * rawProgress * (3 - 2 * rawProgress);

      mediaFrame.style.setProperty("--story-mobile-image-progress", easedProgress.toFixed(3));
      mediaFrame.style.setProperty(
        "--story-mobile-incoming-scale",
        (1.045 - easedProgress * 0.03).toFixed(3),
      );
      mediaFrame.style.setProperty(
        "--story-mobile-outgoing-scale",
        (1 + easedProgress * 0.018).toFixed(3),
      );
      mediaFrame.style.setProperty(
        "--story-mobile-outgoing-opacity",
        (1 - easedProgress * 0.18).toFixed(3),
      );
    };

    const requestRender = () => {
      if (!frameId) {
        frameId = window.requestAnimationFrame(render);
      }
    };

    render();
    window.addEventListener("scroll", requestRender, { passive: true });
    window.addEventListener("resize", requestRender);
    mobileQuery.addEventListener("change", requestRender);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }

      window.removeEventListener("scroll", requestRender);
      window.removeEventListener("resize", requestRender);
      mobileQuery.removeEventListener("change", requestRender);
    };
  }, []);

  return (
    <div ref={shellRef} className="panel-inner story-shell">
      <div className="story-media" data-chrome-surface="dark">
        <div
          className="story-collage-base surface-frame"
          style={backgroundStyle(content.base_image)}
        />

        <div className="story-collage-top">
          <div
            className="story-collage-top-image surface-frame"
            id="storyTopOutgoing"
            style={backgroundStyle(content.top_image)}
          />
        </div>

        <article ref={mediaFrameRef} className="story-collage-middle" id="storyMediaShell">
          <div
            className="story-collage-middle-incoming surface-frame"
            id="storyMiddleIncoming"
            style={backgroundStyle(content.middle_incoming_image)}
          />
          <div
            className="story-collage-middle-outgoing surface-frame"
            id="storyMiddleOutgoing"
            style={backgroundStyle(content.middle_outgoing_image)}
          />
        </article>

        <article
          className="story-collage-bottom surface-frame"
          id="storyBottomAccent"
          style={backgroundStyle(content.bottom_image)}
        />
      </div>

      <div className="story-copy" data-chrome-surface="light">
        <h2 className="story-headline" data-reveal style={{ ["--reveal-delay" as string]: "80ms" }}>
          <span>{content.headline_one}</span>
          <span>{content.headline_two}</span>
          <span className="story-headline-motion">
            <span className="story-headline-motion-inner">{content.headline_motion}</span>
          </span>
        </h2>
        <p data-reveal style={{ ["--reveal-delay" as string]: "180ms" }}>
          {content.paragraph}
        </p>
        <div className="story-signature" data-reveal style={{ ["--reveal-delay" as string]: "260ms" }}>
          <span>{content.signature_one}</span>
          <span>{content.signature_two}</span>
        </div>
      </div>

      <div className="story-headline-rail-target" aria-hidden="true" />
    </div>
  );
}
